import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type CalculateCommissionResult,
  type ListCommissionPlansResult,
  type ListPeopleResult,
} from '@whinerp/client';
import { formatMoney, formatPercent } from '@whinerp/contracts/format';
import { Button, ErrorState, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-80 Commission (G-147): what a person earned in a period, band by band,
 * and - for revenue managers - the plans and who is on each.
 *
 * Anybody sees their own. The arithmetic is on the screen, because a
 * commission figure nobody can check is the one that gets disputed.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

/** The last four calendar quarters, newest first. */
function quarters(): Array<{ start: string; end: string; label: string }> {
  const now = new Date();
  let year = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3);
  const out: Array<{ start: string; end: string; label: string }> = [];
  for (let i = 0; i < 4; i += 1) {
    const startMonth = q * 3;
    const start = `${String(year)}-${String(startMonth + 1).padStart(2, '0')}-01`;
    const endDate = new Date(Date.UTC(year, startMonth + 3, 0));
    out.push({ start, end: endDate.toISOString().slice(0, 10), label: `Q${String(q + 1)} ${String(year)}` });
    q -= 1;
    if (q < 0) {
      q = 3;
      year -= 1;
    }
  }
  return out;
}

/** "100:15, 120:20" - from what share of target, at what rate. */
function parseTiers(text: string): Array<{ fromAttainment: number; percent: number }> {
  return text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [from, rate] = part.split(':').map((value) => Number(value.trim()));
      return { fromAttainment: from ?? NaN, percent: rate ?? NaN };
    });
}

function CommissionScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { session, can } = useSession();
  const seesOthers = can('revenue.view');
  const manages = can('revenue.manage');
  const periods = quarters();
  const [userId, setUserId] = useState(session.user.id);
  const [period, setPeriod] = useState(periods[0]!);

  const plans = useQuery<ListCommissionPlansResult, ApiError>({
    queryKey: ['commission-plans'],
    queryFn: () => api.listCommissionPlans({}),
    enabled: seesOthers,
  });
  const key = ['commission', userId, period.start, period.end];
  const statement = useQuery<CalculateCommissionResult, ApiError>({
    queryKey: key,
    queryFn: () => api.calculateCommission({ query: { userId, periodStart: period.start, periodEnd: period.end } }),
    placeholderData: (previous) => previous,
  });
  const approve = useMutation({
    mutationFn: () => api.approveCommissionStatement({ body: { userId, periodStart: period.start, periodEnd: period.end } }),
    onSuccess: (result) => queryClient.setQueryData(key, result),
  });

  const people = new Map<string, string>([[session.user.id, t('commission.me')]]);
  for (const plan of plans.data?.plans ?? []) for (const member of plan.members) if (!people.has(member.userId)) people.set(member.userId, member.name);

  if (statement.isPending) return <Skeleton className="h-plot w-full" />;
  if (statement.isError) {
    return <ErrorState title={t('state.error')} body={errorText(statement.error)} onRetry={() => void statement.refetch()} />;
  }
  const view = statement.data;
  const money = (amount: string) => formatMoney(amount, format, { currency: view.currency ?? 'AED' });

  return (
    <div data-component="Commission" className="flex w-full flex-col gap-4">
      <PageHeading>{t('commission.title')}</PageHeading>
      <div className="flex flex-wrap gap-2">
        {seesOthers ? (
          <select aria-label={t('commission.person')} className={INPUT} data-commission-person value={userId}
            onChange={(event) => setUserId(event.target.value)}>
            {[...people].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        ) : null}
        <select aria-label={t('commission.period')} className={INPUT} data-commission-period value={period.start}
          onChange={(event) => setPeriod(periods.find((entry) => entry.start === event.target.value) ?? periods[0]!)}>
          {periods.map((entry) => <option key={entry.start} value={entry.start}>{entry.label}</option>)}
        </select>
      </div>

      <Panel data-region="commission-statement">
        {view.planId === null ? (
          <p className="text-body text-ink-meta" data-commission-no-plan>{t('commission.noPlan', { values: { name: view.userName } })}</p>
        ) : (
          <div className="flex flex-col gap-3">
            <SectionLabel>{t('commission.onPlan', { values: { plan: view.planName ?? '' } })}</SectionLabel>
            <dl className="grid grid-cols-2 gap-2 text-dense sm:grid-cols-4">
              <div><dt className="text-meta text-ink-meta">{t('commission.credited')}</dt><dd data-commission-credited>{money(view.credited)}</dd></div>
              <div><dt className="text-meta text-ink-meta">{t('commission.target')}</dt><dd>{view.target === null ? t('commission.noTarget') : money(view.target)}</dd></div>
              <div><dt className="text-meta text-ink-meta">{t('commission.attainment')}</dt><dd>{view.attainment === null ? '-' : formatPercent(view.attainment, format)}</dd></div>
              <div><dt className="text-meta text-ink-meta">{t('commission.payout')}</dt><dd data-commission-payout className="text-ink">{money(view.payout)}</dd></div>
            </dl>
            <table className="w-full text-dense" data-commission-bands>
              <thead>
                <tr className="text-meta text-ink-meta">
                  <th className="p-1 text-start font-normal">{t('commission.band')}</th>
                  <th className="p-1 text-end font-normal">{t('commission.amount')}</th>
                  <th className="p-1 text-end font-normal">{t('commission.rate')}</th>
                  <th className="p-1 text-end font-normal">{t('commission.payout')}</th>
                </tr>
              </thead>
              <tbody>
                {view.bands.map((entry, index) => (
                  <tr key={index} data-commission-band={index}>
                    <td className="p-1">
                      {entry.fromAttainment === null || entry.fromAttainment === 0
                        ? t('commission.base')
                        : t('commission.above', { values: { from: String(entry.fromAttainment) } })}
                    </td>
                    <td className="p-1 text-end">{money(entry.amount)}</td>
                    <td className="p-1 text-end">{entry.percent}%</td>
                    <td className="p-1 text-end" data-band-payout>{money(entry.payout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {view.approved ? (
              <p className="text-meta text-ink-meta" data-commission-approved>
                {Number(view.approved.difference) === 0
                  ? t('commission.approved', { values: { amount: money(view.approved.payout) } })
                  : t('commission.approvedDiffers', {
                      values: { amount: money(view.approved.payout), difference: money(view.approved.difference) },
                    })}
              </p>
            ) : manages ? (
              <div>
                <Button size="small" intent="primary" data-commission-approve disabled={approve.isPending} onClick={() => approve.mutate()}>
                  {t('commission.approve')}
                </Button>
              </div>
            ) : null}
            {approve.error ? <p className="text-dense text-danger-text">{errorText(approve.error as ApiError)}</p> : null}
          </div>
        )}
      </Panel>

      {view.deals.length > 0 || view.excluded.length > 0 ? (
        <Panel data-region="commission-deals">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('commission.deals')}</SectionLabel>
            <ul className="flex flex-col gap-1 text-dense">
              {view.deals.map((deal) => (
                <li key={deal.dealId} data-commission-deal={deal.dealId} className="flex flex-wrap gap-2">
                  <span className="text-ink">{deal.name}</span>
                  <span className="text-ink-meta">{deal.closedOn}</span>
                  <span className="text-ink-meta">{t('commission.split', { values: { split: String(deal.splitPercent), amount: money(deal.amount) } })}</span>
                  <span className="ms-auto text-ink">{money(deal.credit)}</span>
                </li>
              ))}
              {view.excluded.map((deal) => (
                <li key={deal.dealId} data-commission-excluded={deal.dealId} className="flex flex-wrap gap-2 text-ink-meta">
                  <span>{deal.name}</span>
                  <span>{t('commission.leftOut', { values: { reason: deal.reason } })}</span>
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}

      {manages ? <PlansPanel plans={plans.data?.plans ?? []} /> : null}
    </div>
  );
}

function PlansPanel({ plans }: { plans: ListCommissionPlansResult['plans'] }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { session, can } = useSession();
  // Everybody in the workspace when this person may see them; otherwise only themselves.
  const people = useQuery<ListPeopleResult, ApiError>({
    queryKey: ['people'],
    queryFn: () => api.listPeople({}),
    enabled: can('setup.users.manage'),
  });
  const choices = people.data
    ? people.data.people.map((person) => ({ userId: person.userId, name: person.name }))
    : [{ userId: session.user.id, name: t('commission.me') }];
  const [who, setWho] = useState(session.user.id);
  const [draft, setDraft] = useState({ name: '', currency: 'AED', base: '', tiers: '' });
  const save = useMutation({
    mutationFn: () =>
      api.saveCommissionPlan({
        body: { planId: null, name: draft.name, currency: draft.currency, basePercent: Number(draft.base), tiers: parseTiers(draft.tiers) },
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(['commission-plans'], result);
      setDraft({ name: '', currency: 'AED', base: '', tiers: '' });
    },
  });
  const assign = useMutation({
    mutationFn: (input: { planId: string }) => api.assignCommissionPlan({ body: { userId: who, planId: input.planId } }),
    onSuccess: (result) => {
      queryClient.setQueryData(['commission-plans'], result);
      void queryClient.invalidateQueries({ queryKey: ['commission'] });
    },
  });
  return (
    <Panel data-region="commission-plans">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('commission.plans')}</SectionLabel>
        <label className="flex items-center gap-2 text-meta text-ink-meta">
          {t('commission.assignWho')}
          <select className={INPUT} data-commission-assign-who value={who} onChange={(event) => setWho(event.target.value)}>
            {choices.map((person) => <option key={person.userId} value={person.userId}>{person.name}</option>)}
          </select>
        </label>
        <ul className="flex flex-col gap-1 text-dense">
          {plans.map((plan) => (
            <li key={plan.planId} data-commission-plan={plan.planId} className="flex flex-wrap items-center gap-2">
              <span className="text-ink">{plan.name}</span>
              <span className="text-ink-meta">
                {t('commission.planRates', {
                  values: {
                    base: plan.basePercent,
                    tiers: plan.tiers.map((tier) => `${String(tier.percent)}% > ${String(tier.fromAttainment)}%`).join(', ') || '-',
                  },
                })}
              </span>
              <span className="text-ink-meta">{plan.members.map((member) => member.name).join(', ')}</span>
              <Button size="small" data-commission-join={plan.planId} onClick={() => assign.mutate({ planId: plan.planId })}>
                {t('commission.putOn')}
              </Button>
            </li>
          ))}
        </ul>
        <form data-commission-plan-form className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('commission.planName')}
            <input className={INPUT} required maxLength={120} data-plan-name value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('commission.currency')}
            <input className={`${INPUT} w-20`} required maxLength={3} data-plan-currency value={draft.currency}
              onChange={(event) => setDraft({ ...draft, currency: event.target.value.toUpperCase() })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('commission.basePercent')}
            <input className={`${INPUT} w-20`} required inputMode="decimal" data-plan-base value={draft.base}
              onChange={(event) => setDraft({ ...draft, base: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('commission.tiers')}
            <input className={INPUT} placeholder="100:15, 120:20" data-plan-tiers value={draft.tiers}
              onChange={(event) => setDraft({ ...draft, tiers: event.target.value })} />
          </label>
          <Button size="small" type="submit" data-plan-save disabled={save.isPending}>{t('commission.savePlan')}</Button>
          {save.error ? <p className="w-full text-dense text-danger-text" data-plan-error>{errorText(save.error as ApiError)}</p> : null}
          {assign.error ? <p className="w-full text-dense text-danger-text">{errorText(assign.error as ApiError)}</p> : null}
        </form>
      </div>
    </Panel>
  );
}

function Loading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function Failed(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('commission.failed')} />;
}

export const screen = defineScreen({
  id: 'SC-80',
  route: '/revenue/commission',
  surface: 'page',
  title: 'Commission',
  features: ['F-080'],
  entitlement: 'F-080',
  reachedBy: [{ kind: 'screen', from: 'SC-45', how: 'Commission link on the revenue screen' }],
  components: [],
  elements: ['Commission'],
  states: { empty: Loading, loading: Loading, error: Failed },
  component: CommissionScreen,
});
